//
// Copyright (c) 2024 Emplifi
//

define([
  "backbone",
  "i18n/i18n",
  "marionette",
  "messaging/messenger",
  "./apiKeyDetailsView",
  "modules/accountAdmin/apiKeyAdmin/apiKeyModel",
], function (Backbone, i18next, Marionette, Messenger, ApiKeyDetailsView, ApiKeyModel) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "apiKeyDetails";
      this.tenant = options.tenant;
      this._setUpVents();
    },
    show: function (options) {
      if (options.apiKey) {
        this.apiKey = options.apiKey;
      } else if (options.apiKeyId) {
        const apiKeys = Messenger.reqres.request("tenant:apiKeys", {
          tenantId: this.tenant.id,
        });
        apiKeys.onSynced(() => {
          let apiKey;
          if (options.apiKeyId == "new") {
            apiKey = apiKeys.addApiKey();
          } else {
            apiKey = apiKeys.get(options.apiKeyId);
          }
          this.show({
            apiKey: apiKey,
          });
        });
        return;
      } else {
        Backbone.history.navigate(`accountAdmin/tenants/${this.tenant.id}/apiKeys`, {
          trigger: true,
        });
        return;
      }

      if (this.apiKey.isNew()) {
        Backbone.history.navigate(`accountAdmin/tenants/${this.tenant.id}/apiKeys/new`);
      } else {
        Backbone.history.navigate(
          `accountAdmin/tenants/${this.tenant.id}/apiKeys/${this.apiKey.id}`,
        );
        // Make sure we have the most recent version of the API Key
        this.apiKey.fetch();
      }

      Messenger.onSave(this._onSave, this);

      this.allowedRoles = Messenger.reqres.request("apiKey:tenant:allowedRoles", this.tenant.id);
      this.apiKey.kbs = Messenger.reqres.request("tenant:kbs", this.tenant.id);
      this.originalName = this.apiKey.get("name");
      this.view = new ApiKeyDetailsView.view({
        model: this.apiKey,
      });
      this.view.on("copy:apiKey", this._onCopyApiKey, this);
      this.view.on("revoke:apiKey", this._onRevokeApiKey, this);
      this.view.on("delete:apiKey", this._onDeleteApiKey, this);
      Promise.all([
        this.allowedRoles.onSyncedPromise(),
        this.apiKey.onSyncedPromise(),
        this.apiKey.kbs.onSyncedPromise(),
      ])
        .then(() => this._setUpData())
        .then(() => this.showRegion(this.view));
    },
    _onCopyApiKey: function () {
      try {
        Messenger.vent.trigger("track:interaction", {
          action: "Copy API Key",
          id: this.apiKey.id,
          idType: "API Key",
          object: "API Key",
          source: "API Key Details",
        });
        navigator.clipboard
          .writeText(this.apiKey.get("value"))
          .then(() => Messenger.okDialog(i18next.t("apiKey.copySuccess")))
          .catch((e) => {
            console.error("Failed to copy API Key to clipboard", e);
            Messenger.okDialog(i18next.t("apiKey.copyFailedError"));
          });
      } catch (e) {
        console.error("Failed to copy API Key to clipboard", e);
        Messenger.okDialog(i18next.t("apiKey.copyFailedError"));
      }
    },
    _onRevokeApiKey: function () {
      Messenger.yesNoDialog(i18next.t("apiKey.confirmRevokeApiKey"), {
        yes: () => {
          Messenger.vent.trigger("track:interaction", {
            action: "Revoke API Key",
            id: this.apiKey.id,
            idType: "API Key",
            object: "API Key",
            source: "API Key Details",
          });
          this.apiKey.revokeApiKey().then(() => this.view.render());
        },
      });
    },
    _onSave: function () {
      this.view.clearErrors();
      const name = this.view.getName();
      if (name.length === 0) {
        this.view.showNameError(i18next.t("commonMessages.emptyMessage"));
        Messenger.saveAttempted(false, true);
        return;
      }
      if (this.apiKey.isNew()) {
        const kbId = this.view.getKnowledgeBase();
        if (kbId <= 0) {
          this.view.showKnowledgeBaseError(i18next.t("commonMessages.emptyMessage"));
          Messenger.saveAttempted(false, true);
          return;
        }
        const expiresAt = this.view.getExpiresAt();
        const value = this.view.getValue();
        this.apiKey.set("name", name);
        if (value) {
          this.apiKey.set("value", value);
        }
        if (expiresAt) {
          this.apiKey.set("expiresAt", expiresAt);
        }
        this.apiKey.set("revoked", this.view.getRevoked());
        // A new API Key should have no roles, so add the roles that were configured
        if (this.view.getRoleEngine()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.ENGINE);
        }
        if (this.view.getRoleFordApi()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.FORD_API);
        }
        if (this.view.getRoleTopicExportApi()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.TOPIC_EXPORT_API);
        }
        if (this.view.getRoleTopicImportApi()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.TOPIC_IMPORT_API);
        }
        if (this.view.getRoleTopicSearchApi()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.TOPIC_SEARCH_API);
        }
        if (this.view.getRoleTopicPublishApi()) {
          this.apiKey.roles().addRole(kbId, ApiKeyModel.role.ROLE_ENUM.TOPIC_PUBLISH_API);
        }

        if (this.apiKey.roles().length === 0) {
          Messenger.okDialog(i18next.t("apiKey.rolesRequiredError"));
          Messenger.saveAttempted(false, true);
          return;
        }

        this.apiKey.saveModel();
      } else if (name != this.originalName) {
        this.apiKey.renameApiKey(name).then();
      } else {
        Messenger.saveAttempted(true);
      }
    },
    _setUpData: function () {
      this.apiKey.kbId = 0;
      this.apiKey.allowRoleEngine = false;
      this.apiKey.allowRoleFordApi = false;
      this.apiKey.allowRoleTopicExportApi = false;
      this.apiKey.allowRoleTopicImportApi = false;
      this.apiKey.allowRoleTopicSearchApi = false;
      this.apiKey.allowRoleTopicPublishApi = false;
      this.apiKey.roleEngine = false;
      this.apiKey.roleFordApi = false;
      this.apiKey.roleTopicExportApi = false;
      this.apiKey.roleTopicImportApi = false;
      this.apiKey.roleTopicSearchApi = false;
      this.apiKey.roleTopicPublishApi = false;
      this.apiKey.readonly = !this.apiKey.isNew();
      this.allowedRoles.forEach((allowedRole) => {
        switch (allowedRole.get("roleId")) {
          case ApiKeyModel.role.ROLE_ENUM.ENGINE:
            this.apiKey.allowedRoleEngine = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.FORD_API:
            this.apiKey.allowedRoleFordApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_EXPORT_API:
            this.apiKey.allowedRoleTopicExportApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_IMPORT_API:
            this.apiKey.allowedRoleTopicImportApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_SEARCH_API:
            this.apiKey.allowedRoleTopicSearchApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_PUBLISH_API:
            this.apiKey.allowedRoleTopicPublishApi = true;
            break;
        }
      });
      this.apiKey.roles().forEach((role) => {
        this.apiKey.kbId = role.get("kbId");
        switch (role.get("roleId")) {
          case ApiKeyModel.role.ROLE_ENUM.ENGINE:
            this.apiKey.roleEngine = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.FORD_API:
            this.apiKey.roleFordApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_EXPORT_API:
            this.apiKey.roleTopicExportApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_IMPORT_API:
            this.apiKey.roleTopicImportApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_SEARCH_API:
            this.apiKey.roleTopicSearchApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_PUBLISH_API:
            this.apiKey.roleTopicPublishApi = true;
            break;
        }
      });
    },
    _setUpVents: function () {
      Messenger.vent.on("show:tenant:profile:apiKey", this.show, this);
    },
    _onDeleteApiKey: function () {
      const model = this.apiKey;
      Messenger.yesNoDialog(i18next.t("apiKey.confirmDeleteApiKey", { name: model.get("name") }), {
        yes: () => {
          Messenger.vent.trigger("track:interaction", {
            action: "Delete API Key",
            id: model.id,
            idType: "API Key",
            object: "API Key",
            source: "API Key Details",
          });
          model.deleteModel({
            success: function () {
              Messenger.vent.trigger("show:tenant:profile:apiKeys");
            },
          });
        },
      });
    },
  });
});
